import { Box, Table, Tbody, Td, Text, Th, Thead, Tr } from "@chakra-ui/react";
import BigNumber from "bignumber.js";
import { truncate } from "lodash";
import moment from "moment";
import { useContext, useMemo } from "react";
import { BaseAuditLogFields, BaseTransferFields } from "src/api/fragments";
import { AssetTypeEnum, CurrencyCodeEnum } from "src/api/generated/types";
import { ActiveTransactionContext } from "src/context";
import { useTheme } from "src/hooks/useTheme";
import { formatNum, getCurrencySymbol } from "src/utils/helpers";

export const AuditLogsTable = ({
  auditLogs,
}: {
  auditLogs: BaseAuditLogFields[];
}) => {
  const theme = useTheme();

  return (
    <Box
      bg={theme.background}
      w="100%"
      position="relative"
      borderRadius="0.5rem"
      boxShadow="sm"
    >
      <Box w="100%" maxW="100%" overflowX="scroll" border="none">
        <Text
          fontSize="md"
          fontWeight="medium"
          color={theme.text}
          padding={"15px"}
        >
          Transaction history <i className="fa-solid fa-clock-rotate-left"></i>
        </Text>
        <Table w="100%" maxW="100%" border="none">
          <Thead bg={theme.secondaryBackground} color={theme.header}>
            <Tr>
              <Th
                borderBottom="0 !important"
                style={{ width: 200 }}
                color={theme.header}
              >
                Field
              </Th>
              <Th borderBottom="0 !important" color={theme.header}>
                Previous
              </Th>
              <Th borderBottom="0 !important" color={theme.header}>
                New
              </Th>
              <Th borderBottom="0 !important" color={theme.header} />
            </Tr>
          </Thead>

          <Tbody border="none" w="100%">
            {auditLogs.map((log) => (
              <AuditLogRow log={log} key={log.id} />
            ))}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

const AuditLogRow = ({ log }: { log: BaseAuditLogFields }) => {
  const theme = useTheme();
  const { client, transaction } = useContext(ActiveTransactionContext);
  const currency = client?.currency;

  const transfer = useMemo((): BaseTransferFields | null => {
    const all = transaction?.transfers ?? [];
    if (!log.transferId || !all.length) {
      return null;
    }

    const t = all.find((a) => a.id === log.transferId);
    return t ?? null;
  }, [transaction?.transfers, log.transferId]);

  const formatValue = (field: string, value: string | null | undefined) => {
    if (value === null || value === undefined) return "None";
    if (value?.includes("v1:")) {
      return value.split("v1:")[1];
    }
    // if the value could be a number, format it as a number
    if (!isNaN(Number(value)) && (field === "Basis" || field === "FiatValue")) {
      const adjValue = new BigNumber(value).dividedBy(100);
      const currencySymbol = getCurrencySymbol(
        currency || CurrencyCodeEnum.Usd
      );
      return `${currencySymbol}${adjValue.toFormat(2)}`;
    }
    return value;
  };

  const formatField = (field: string) => {
    if (field === "FiatValue") {
      return "Sell Price";
    }
    if (field === "Basis") {
      return "Cost Basis";
    }
    return field
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  return (
    <Tr
      sx={{
        borderBottom: "none",
        "&:hover": {
          bg: theme.medBackground,
        },
        transition: "background-color 0.2s",
      }}
    >
      <Td
        color={theme.text}
        style={{
          fontStretch: "normal",
        }}
        borderBottom="0"
        fontSize="sm"
      >
        <Text fontWeight="medium" fontSize="sm">
          {formatField(log.field)}
        </Text>
        {transfer && (
          <Text fontWeight="normal" fontSize="xs">
            {formatNum(transfer.value, false)}{" "}
            {transfer.fullAsset?.type === AssetTypeEnum.Nft
              ? transfer.fullAsset?.name
              : transfer.fullAsset?.symbol}
          </Text>
        )}
      </Td>
      <Td
        fontWeight={"normal"}
        color={theme.text}
        borderBottom="0"
        fontSize="sm"
      >
        {truncate(formatValue(log.field, log.oldValue), { length: 30 })}
      </Td>
      <Td
        fontWeight={"normal"}
        color={theme.text}
        borderBottom="0"
        fontSize="sm"
      >
        {truncate(formatValue(log.field, log.newValue), { length: 30 })}
      </Td>
      <Td
        color={theme.text}
        fontWeight={"normal"}
        borderBottom="0"
        fontSize="sm"
      >
        {moment(new Date(log.createdAt)).format("h:mma")}
        <br />
        {moment(new Date(log.createdAt)).format("M/D/YYYY")}
      </Td>
    </Tr>
  );
};
